import React, { useEffect, useState } from 'react';
import { Box, Button, Link, Typography, alpha, useTheme } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import axios from 'axios';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import config from '../config';
import { useAuth } from '../contexts/AuthContext';
import MillosLogo from './MillosLogo';

const TERRACOTTA = '#C87A53';
const TERRACOTTA_DARK = '#B46A44';

// The "check your email" step, on its own route (/check-email). Guards send unverified users here,
// and it sends them on (to where they were heading) the moment the address is confirmed.
const VerifyEmailGate: React.FC = () => {
  const { user, token, loading, refreshUser, logout } = useAuth();
  const { palette } = useTheme();
  const t = palette.tokens;
  const navigate = useNavigate();
  const location = useLocation();
  const next = sessionStorage.getItem('after_verify_next') || (location.state as { next?: string } | null)?.next || '/dashboard';
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  const verified = Boolean(user) && user?.email_verified !== false;

  // The link is opened in another tab or on a phone, so keep checking until it has been used
  useEffect(() => {
    if (verified) return;
    const timer = setInterval(() => { if (!document.hidden) refreshUser(); }, 4000);
    window.addEventListener('focus', refreshUser);
    return () => { clearInterval(timer); window.removeEventListener('focus', refreshUser); };
  }, [verified, refreshUser]);

  useEffect(() => {
    if (!verified) return;
    sessionStorage.removeItem('after_verify_next');
    navigate(next, { replace: true });
  }, [verified, next, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  const resend = async () => {
    setSending(true);
    setMessage(null);
    try {
      await axios.post(`${config.apiUrl}/api/resend-verification`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setMessage('Sent. Check your inbox.');
      setCooldown(60);
    } catch (err: any) {
      setMessage(err.response?.data?.error || 'Could not send the email. Please try again.');
      if (err.response?.data?.retry_after) setCooldown(err.response.data.retry_after);
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: t.canvas, p: 2 }}>
      <Box
        component="main"
        sx={{
          width: '100%', maxWidth: 480, bgcolor: t.white, borderRadius: '24px', border: `1px solid ${t.hairline}`,
          boxShadow: '0 12px 40px rgba(60, 40, 25, 0.08)', px: { xs: 3, sm: 6 }, py: { xs: 5, sm: 7 }, textAlign: 'center',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <MillosLogo size={44} />
        </Box>

        <Box
          aria-hidden
          sx={{ width: 72, height: 72, mx: 'auto', mb: 3, borderRadius: '50%', bgcolor: alpha(TERRACOTTA, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <MailOutlineIcon sx={{ fontSize: 34, color: TERRACOTTA }} />
        </Box>

        <Typography component="h1" sx={{ fontSize: { xs: '1.65rem', sm: '1.9rem' }, fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1.2, color: t.ink, mb: 2 }}>
          Check your email
        </Typography>
        <Typography sx={{ fontSize: '16px', lineHeight: 1.65, color: t.muted, mb: 0.5 }}>
          We've sent a verification link to
        </Typography>
        <Typography noWrap title={user.email} sx={{ fontSize: '16px', fontWeight: 600, color: t.ink, mb: 2 }}>
          {user.email}
        </Typography>
        <Typography sx={{ fontSize: '15px', lineHeight: 1.65, color: t.muted, mb: 5 }}>
          Click the button in that email to confirm your address. This page moves on by itself once you have.
        </Typography>

        <Button
          onClick={resend}
          disabled={sending || cooldown > 0}
          sx={{
            textTransform: 'none', borderRadius: 999, px: 6, py: 1.6, minWidth: 220, fontSize: '16px', fontWeight: 700, letterSpacing: '0.01em',
            color: '#fff', bgcolor: TERRACOTTA, boxShadow: '0 8px 20px rgba(200, 122, 83, 0.35)',
            '&:hover': { bgcolor: TERRACOTTA_DARK, boxShadow: '0 10px 24px rgba(200, 122, 83, 0.45)' },
            '&:active': { boxShadow: '0 4px 10px rgba(200, 122, 83, 0.35)' },
            '&.Mui-disabled': { color: '#fff', bgcolor: alpha(TERRACOTTA, 0.55), boxShadow: 'none' },
          }}
        >
          {cooldown > 0 ? `Resend email (${cooldown}s)` : 'Resend email'}
        </Button>

        <Box sx={{ minHeight: 24, mt: 2 }}>
          {message && <Typography role="status" sx={{ fontSize: '14px', color: t.muted }}>{message}</Typography>}
        </Box>
        <Typography sx={{ fontSize: '13px', fontWeight: 300, color: t.mutedSoft, mb: 6 }}>
          Can't find it? Check your spam folder.
        </Typography>

        <Link component="button" type="button" underline="always" onClick={logout} sx={{ fontSize: '14px', color: t.muted }}>
          Sign out
        </Link>
      </Box>
    </Box>
  );
};

// Sends the signed-in user to /check-email while their address is unconfirmed
export const RequireVerifiedEmail: React.FC<{ children: React.ReactNode; next?: string }> = ({ children, next = '/dashboard' }) => {
  const { user } = useAuth();
  if (user && user.email_verified === false) return <Navigate to="/check-email" replace state={{ next }} />;
  return <>{children}</>;
};

export default VerifyEmailGate;
